/**
 * GlobalAmbient — единый ambient-слой для всего сайта.
 *
 * Содержит:
 *   • .app-atmosphere — fixed multi-radial gradient (subtle baseline tint
 *     по углам viewport'а, не отвлекает от content)
 *   • .app-orbs — кластер floating soft sphere'ов в upper-center viewport
 *     с scroll-fade (через --app-orbs-opacity, обновляется window scroll)
 *   • Brand-reactive: data-active-brand на корне → CSS-vars (--brand-glow*)
 *     из _app-ambient.scss перекрашивают brand orb и подсвечивают site orbs
 *
 * Mount: один раз на уровне App. Раньше это всё жило в HomeTemp.tsx (только
 * главная), но визуальная атмосфера нужна на всех страницах кроме auth —
 * перенесли сюда чтобы избежать дубля.
 *
 * Auth-страницы (login/register/forgot-password/verify/telegram-callback)
 * имеют собственный layout БЕЗ main-content wrapper — там ambient был бы
 * лишним. Проверяем через useLocation.
 */
import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { detectBrand } from '../data/brands'
import type { Model } from '../data/models'
import { backgroundOf } from '../utils/routeModal'

interface Props {
	/** Текущая выбранная модель — для brand-reactive окраски orbs. */
	selectedModel: Model | null
}

// Pathnames где ambient скрывается. startsWith match — покрывает варианты
// /auth/telegram-link-callback, /auth/telegram-callback и т.п.
const AUTH_PATH_PREFIXES = [
	'/login',
	'/register',
	'/forgot-password',
	'/verify',
	'/verify-pending',
	'/confirm-email',
	'/auth/',
]

export default function GlobalAmbient({ selectedModel }: Props) {
	const location = useLocation()
	// Окно входа поверх страницы (/register со страницей в state) — фон остаётся фоном той страницы
	const pathname = (backgroundOf(location) ?? location).pathname
	const isAuthRoute = AUTH_PATH_PREFIXES.some((p) => pathname.startsWith(p))

	// Brand resolution. selectedModel может быть null (юзер не выбрал) или
	// non-text модель из /generate — в обоих случаях detectBrand вернёт
	// что-то осмысленное или null → fallback к пустой строке (idle purple).
	const activeBrandId = useMemo(() => {
		if (!selectedModel) return ''
		const brand = detectBrand(selectedModel.provider, selectedModel.id)
		return brand?.id ?? ''
	}, [selectedModel])

	// Scroll-fade orbs: при скролле страницы вниз orbs (position: fixed на
	// viewport) накладываются на content (popular/FAQ/cards/grid) и мешают.
	// Линейно уменьшаем opacity 1 → 0 в диапазоне 100-600px scrollY.
	// passive listener — не блокирует scroll-thread.
	// Переменная пишется на сам .app-orbs, а не на :root: на корне её наследует каждый элемент, и любое
	// событие прокрутки в этом диапазоне пересчитывало стили всей страницы — на iPhone как раз во время
	// прокрутки (02.10: ЛК «ниже первого экрана всё чёрное»). Одинаковое значение не пишем вовсе.
	const orbsRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (isAuthRoute) return
		const el = orbsRef.current
		if (!el) return
		const FADE_START = 100
		const FADE_END = 600
		let last = ''
		const onScroll = () => {
			const y = window.scrollY
			const t = Math.max(0, Math.min(1, (y - FADE_START) / (FADE_END - FADE_START)))
			const opacity = String(1 - t)
			if (opacity === last) return
			last = opacity
			el.style.setProperty('--app-orbs-opacity', opacity)
		}
		onScroll() // initial calc
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => {
			window.removeEventListener('scroll', onScroll)
			el.style.removeProperty('--app-orbs-opacity')
		}
	}, [isAuthRoute])

	if (isAuthRoute) return null

	return (
		<div className="app-ambient" data-active-brand={activeBrandId} aria-hidden="true">
			{/* Базовый multi-radial gradient — даёт страница не плоский серый,
			    а subtle цветовую глубину по углам. Виден всегда (не fade'ит). */}
			<div className="app-atmosphere" />

			{/* Floating sphere cluster — 6 site orbs + 1 brand orb. Brand orb
			    fade-in'ит цвет выбранной модели (через --brand-glow-ring var
			    которая берётся от data-active-brand на .app-ambient root). */}
			<div className="app-orbs" ref={orbsRef}>
				<span className="app-orb app-orb--1" />
				<span className="app-orb app-orb--2" />
				<span className="app-orb app-orb--3" />
				<span className="app-orb app-orb--4" />
				<span className="app-orb app-orb--5" />
				<span className="app-orb app-orb--6" />
				<span className="app-orb app-orb--brand" />
			</div>
		</div>
	)
}
